import React, { useState } from 'react';
import { PauseCircle, PlayCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import rentalsService from '../services/rentalsService';
import { apiErrorText } from '../utils/apiError';

/**
 * Pause / resume a rental.
 *
 * A tenant says they have moved out; staff need a couple of days to check
 * the unit. Pausing stops everything the SYSTEM would do to that rental in
 * the meantime — invoices, late fees, autopay, reminders, texts — so a
 * person who already left is not billed and chased while the office
 * verifies. It ends one of two ways: Move Out (dated the day they reported
 * leaving) or Resume.
 *
 * mode: 'pause' | 'resume'
 */
const todayLocal = (timeZone) => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone || undefined })
      .format(new Date());
  } catch (_) {
    return new Date().toISOString().slice(0, 10);
  }
};

const inputCls =
  'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500';

const RentalHoldModal = ({ mode, rental, facilityId, timeZone, onClose, onDone }) => {
  const max = todayLocal(timeZone);
  const [reportedOn, setReportedOn] = useState(max);
  const [note, setNote] = useState('');
  const [waive, setWaive] = useState(true);
  const [busy, setBusy] = useState(false);
  const pausing = mode === 'pause';
  const who = rental?.customer_name || 'this tenant';
  const unit = rental?.unit_number ? `unit ${rental.unit_number}` : 'this unit';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (pausing) {
        await rentalsService.pause(facilityId, rental._id, {
          move_out_reported_on: reportedOn || undefined,
          note: note.trim() || undefined,
        });
        toast.success('Rental paused. Billing and messages are on hold.');
      } else {
        const { data } = await rentalsService.resume(facilityId, rental._id, {
          waive_missed_fees: waive,
          note: note.trim() || undefined,
        });
        const bits = [];
        if (data?.invoices_minted) bits.push(`${data.invoices_minted} invoice${data.invoices_minted === 1 ? '' : 's'} issued`);
        if (data?.waived_fee_tiers) bits.push(`${data.waived_fee_tiers} late fee${data.waived_fee_tiers === 1 ? '' : 's'} waived`);
        toast.success(`Rental resumed${bits.length ? ` — ${bits.join(', ')}` : ''}.`);
      }
      if (typeof onDone === 'function') onDone();
      onClose();
    } catch (err) {
      toast.error(apiErrorText(err, pausing ? 'Could not pause the rental.' : 'Could not resume the rental.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         role="dialog" aria-modal="true" data-testid="rental-hold-modal">
      <form onSubmit={submit} className="w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h3 className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
            {pausing
              ? <PauseCircle className="h-4 w-4 text-amber-600" />
              : <PlayCircle className="h-4 w-4 text-emerald-600" />}
            {pausing ? 'Pause rental' : 'Resume rental'}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {pausing ? (
            <>
              <p className="text-[13px] text-slate-600 leading-relaxed">
                Use this when {who} says they have moved out of {unit} and you still need to
                check. While paused, <strong>no invoices, late fees, autopay charges, reminders
                or texts</strong> go out for this rental, and the gate stays as it is. The tenant
                can still pay, and you can still do anything by hand.
              </p>
              <div>
                <label className="block text-[12px] font-medium text-slate-700 mb-1">
                  Day the tenant says they moved out
                </label>
                <input type="date" value={reportedOn} max={max} required
                       onChange={(e) => setReportedOn(e.target.value)} className={inputCls} />
                <p className="text-[11px] text-slate-400 mt-1">
                  When you confirm the move-out, the rental ends on this day.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px] text-slate-600 leading-relaxed">
                Resume when {who} has <strong>not</strong> moved out of {unit}. Billing and
                messages start again. An invoice that was due to be issued while the rental was
                paused is issued now.
              </p>
              <label className="flex items-start gap-2 text-[13px] text-slate-700">
                <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)}
                       className="mt-0.5" />
                <span>
                  Waive late fees that came due while the rental was paused
                  <span className="block text-[11px] text-slate-400">
                    Unticked, those fees are charged on the next fee run.
                  </span>
                </span>
              </label>
            </>
          )}
          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">
              Note <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <textarea rows={2} value={note} maxLength={500}
                      onChange={(e) => setNote(e.target.value)} className={inputCls}
                      placeholder={pausing ? 'Who told you, and how' : 'What you found'} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          <button type="button" onClick={onClose} disabled={busy}
                  className="px-3.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] font-medium">
            Cancel
          </button>
          <button type="submit" disabled={busy}
                  className="px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-medium whitespace-nowrap">
            {busy ? 'Saving…' : pausing ? 'Pause rental' : 'Resume rental'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RentalHoldModal;
